import {
  ConsentCategory,
  getAllPreferences,
  getCategoryPreferences,
  getIntegrations,
  type TConsentCategory,
} from 'public/assets/js/analytics/utilities';
import { SEGMENT_WRITE_KEY } from 'src/env';
import { ensureAnalyticsAvailable } from 'src/lib/analytics-utils';
import segmentDestinations from './segmentDestinations';

/** Parses `window.OnetrustActiveGroups` string into an array of consent categories. */
export const getActiveConsentCategories = (): TConsentCategory[] => {
  const activeGroupsString: string | undefined =
    typeof window !== 'undefined' ? window.OnetrustActiveGroups : undefined;

  if (!activeGroupsString) {
    return [];
  }

  const activeGroups = activeGroupsString.split(',');
  const consentCategories = Object.values(ConsentCategory);

  return consentCategories.filter((category) => {
    return activeGroups.includes(category);
  });
};

/**
 * Initialize Segment with user's consent preferences.
 * This function can only be called once.
 */
export const initializeAnalytics = async (activeGroups: TConsentCategory[]) => {
  if (!ensureAnalyticsAvailable() || window.analytics.initialized) {
    return;
  }

  // the integrations object manages the data flow to Segment destinations. We set the integrations object based
  // on the user preferences when the page reloads and send it with the analytics.load() call.
  const integrations = getIntegrations(segmentDestinations, activeGroups);

  // gets tracking preferences (which includes third parties) for segment middleware. The middleware passes additional
  // context in segment tracks and page views
  const destinationPreferences = getAllPreferences(
    segmentDestinations,
    activeGroups,
  );
  const categoryPreferences = getCategoryPreferences(activeGroups);

  // Note: the following was introduced with Segment's now-legacy consent manager. This
  // no longer does anything from a functional standpoint, but it does add an audit
  // trail to logged events in Segment. We'll keep for now to be safe.
  window.analytics.addSourceMiddleware(({ payload, next }) => {
    // @see https://www.twilio.com/docs/segment/privacy/consent-management/consent-in-segment-connections#consent-object
    payload.obj.context.consent = {
      categoryPreferences,
      destinationPreferences,
    };
    next(payload);
  });

  window.analytics.load(SEGMENT_WRITE_KEY, {
    integrations,
  });
};

/**
 * Note: the following was introduced with Segment's now-legacy consent manager. This
 * no longer does anything from a functional standpoint, but it does add an audit
 * trail to logged events in Segment. We'll keep for now to be safe.
 */
export const identifyUser = async (activeGroups: TConsentCategory[]) => {
  if (!ensureAnalyticsAvailable()) {
    return;
  }

  const destinationPreferences = getAllPreferences(
    segmentDestinations,
    activeGroups,
  );

  // we set user traits so we can access them on the
  // server-side and from destinations (warehouse) https://github.com/segmentio/consent-manager#segment-consent-manager
  window.analytics.identify({
    destinationTrackingPreferences: destinationPreferences,
  });
};
